class EventBus {
  constructor() {
    this.events = {};
  }

  on(event, listener) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event, listener) {
    if (!this.events[event]) return;

    this.events[event] = this.events[event].filter(l => l !== listener);
  }

  emit(event, data) {
    if (!this.events[event]) return;

    this.events[event].forEach(listener => listener(data));
  }
}

function resolveWebSocketUrl() {
  const { protocol, hostname, port, host } = window.location;
  const socketProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
  const isLocalDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
  const isReplitDevelopment = hostname.endsWith('.replit.dev');
  const configuredUrl = process.env.REACT_APP_WS_URL;
  if (configuredUrl && !isReplitDevelopment) return configuredUrl;

  // Localmente o backend atende em 3001. No Preview do Replit, o proxy do CRA
  // encaminha o upgrade no mesmo host público; portas internas não são públicas.
  if (isLocalDevelopment && port !== '3001') {
    return `${socketProtocol}//${hostname}:3001/ws/quote`;
  }

  return `${socketProtocol}//${host}/ws/quote`;
}

class WebSocketManager {
  constructor(onMessage, onError) {
    this.wsUrl = resolveWebSocketUrl();
    this.socket = null;
    this.onMessage = onMessage;
    this.onError = onError;
    this.isConnecting = false;
    this.messageQueue = [];
    this.subscribedSymbols = new Map();
    this.eventBus = new EventBus();
    this.eventBus.on('message', onMessage);
    this.eventBus.on('error', onError);
    this.retryCount = 0;
    this.pingInterval = null;
    this.checkMarketInterval = null;

    this.initializeMarketCheck();
  }

  // Feriados nacionais + B3 (2025 e 2026)
  static FERIADOS_B3 = new Set([
    // 2025
    '2025-01-01','2025-01-25','2025-03-03','2025-03-04','2025-03-05',
    '2025-04-18','2025-04-21','2025-05-01','2025-06-19','2025-07-09',
    '2025-09-07','2025-10-12','2025-11-02','2025-11-15','2025-11-20',
    '2025-12-24','2025-12-25','2025-12-31',
    // 2026
    '2026-01-01','2026-01-25','2026-02-16','2026-02-17','2026-02-18',
    '2026-04-03','2026-04-21','2026-05-01','2026-06-04','2026-07-09',
    '2026-09-07','2026-10-12','2026-11-02','2026-11-15','2026-11-20',
    '2026-12-24','2026-12-25','2026-12-31',
  ]);

  // Verifica se o mercado B3 está aberto agora (hora de SP, dias úteis, sem feriados)
  isMarketOpen() {
    const agora = new Date();
    const sp = new Date(agora.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));

    const diaSemana = sp.getDay(); // 0=Dom, 6=Sab
    if (diaSemana === 0 || diaSemana === 6) return false;

    const ano  = sp.getFullYear();
    const mes  = String(sp.getMonth() + 1).padStart(2, '0');
    const dia  = String(sp.getDate()).padStart(2, '0');
    const data = `${ano}-${mes}-${dia}`;
    if (WebSocketManager.FERIADOS_B3.has(data)) return false;

    const hora = sp.getHours();
    return hora >= 10 && hora < 17; // 10:00–17:00 BRT
  }

  initializeMarketCheck() {
    clearInterval(this.checkMarketInterval);
    this.checkMarketInterval = setInterval(() => {
      if (this.isMarketOpen() && !this.isConnected() && !this.isConnecting) {
        this.retryCount = 0; // reset para nova janela de tentativas
        this.connect();
      }
    }, 60000); // verifica a cada minuto
  }


  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  async connect() {
    if (!this.isMarketOpen()) {
      console.log('Mercado fechado.');
      this.marketOpen = false;
      return;
    }

    this.marketOpen = true;

    if (this.isConnected()) {
      console.log('WebSocket connection already active');
      return;
    }

    if (this.isConnecting) {
      console.log('Connection attempt already in progress');
      return;
    }

    this.isConnecting = true;
    this.socket = new WebSocket(this.wsUrl);

    // Timeout: se não conectar em 10s, força fechar e reconectar
    this.connectTimeout = setTimeout(() => {
      if (this.isConnecting && this.socket && this.socket.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket connection timeout — forçando reconexão');
        this.socket.close();
      }
    }, 10000);

    this.socket.onopen = () => {
      clearTimeout(this.connectTimeout);
      this.isConnecting = false;
      console.log('WebSocket connected');
      const token = localStorage.getItem('token');
      if (token) this.socket.send(JSON.stringify({ type: 'auth', token }));
    
      // Limpa o intervalo de verificação de horário após conectar
      clearInterval(this.checkMarketInterval);
    
      // Envia mensagens pendentes e símbolos inscritos
      this.flushMessageQueue();
      this.sendSubscribedSymbols();
      this.retryCount = 0;
    
      // Configura o ping para manter a conexão ativa
      this.pingInterval = setInterval(() => {
        if (this.isConnected()) {
          this.socket.send('ping');  // Envia ping a cada 30 segundos
        }
      }, 30000);  // Ping a cada 30 segundos
    };
    



    this.socket.onmessage = (event) => {
      if (event.data === 'AUTHENTICATED') return;
      if (event.data.startsWith('ALERT|')) {
        try {
          window.dispatchEvent(new CustomEvent('fatcat:realtime-alert', {
            detail: JSON.parse(event.data.slice(6)),
          }));
        } catch (error) {
          console.error('Alerta realtime inválido:', error);
        }
        return;
      }
      // Sinal do servidor para recarregar dados após coleta diária
      if (event.data === 'RELOAD') {
        window.dispatchEvent(new CustomEvent('fatcat:data-reload'));
        return;
      }
      if (event.data === 'RELOAD_INTRADAY') {
        window.dispatchEvent(new CustomEvent('fatcat:intraday-reload'));
        return;
      }

      const dataLines = event.data.split('\n');

      // Mapeia as linhas recebidas
      const parsedData = dataLines.map(line => {
        const fields = line.split('|');
        const type = fields[0];  // Tipo de mensagem ("S" ou "O")

        if (type === 'S') {
          // Parsing para mensagens do tipo "S"
          const [, symbol, value, variation, updatedAt, nextUpdateAt] = fields;
          return {
            type: 'S',
            ticker: symbol,
            value: parseFloat(value),
            variation: parseFloat(variation),
            updatedAt: updatedAt || null,
            nextUpdateAt: nextUpdateAt || null,
          };
        } else if (type === 'O') {
          // Parsing para mensagens do tipo "O" (opções)
          const [, symbol, value, variation, bid, ask, mid, financialVol, due_date, category, strike, days_to_maturity] = fields;
          return {
            type: 'O',
            ticker: symbol,
            value: parseFloat(value),
            variation: parseFloat(variation),
            bid: parseFloat(bid),
            ask: parseFloat(ask),
            mid: parseFloat(mid),
            financialVol: parseFloat(financialVol),
            due_date: due_date,
            category: category,
            strike: strike,
            days_to_maturity: days_to_maturity,

          };
        } else {
          // console.error("Unknown message type:", type);
          return null;
        }
      });

      // Remove possíveis entradas nulas (se houver erro no parsing)
      const validParsedData = parsedData.filter(data => data !== null);

      // Emitir os dados processados
      this.eventBus.emit('message', validParsedData);
    };


    this.socket.onerror = (error) => {
      clearTimeout(this.connectTimeout);
      this.isConnecting = false;
      console.error('WebSocket error:', error.message);
      this.eventBus.emit('error', error);
    };

    this.socket.onclose = (event) => {
      console.log('WebSocket closed with code:', event.code, 'reason:', event.reason);
      
      clearTimeout(this.connectTimeout);
      // Limpa o intervalo de ping
      clearInterval(this.pingInterval);
      this.socket = null;
      this.isConnecting = false;
    
      // Reinicia a verificação de mercado para reconectar na próxima abertura
      this.initializeMarketCheck();
    
      // Reconecta apenas se o mercado estiver aberto e a desconexão foi inesperada
      if (event.code !== 1000 && event.code !== 1001 && this.isMarketOpen()) {
        this.reconnect();
      }
    };
    
  }

  reconnect() {
    if (!this.isMarketOpen()) {
      console.log('Mercado fechado. WebSocket não reconectado.');
      return;
    }

    if (this.retryCount >= 5) {
      console.log('WebSocket: máximo de tentativas atingido. Aguardando próxima verificação de mercado.');
      return;
    }

    const delay = Math.min(1000 * (2 ** this.retryCount), 30000);
    console.log(`WebSocketManager: Reconnecting in ${delay / 1000} seconds`);
    setTimeout(() => {
      this.connect();
    }, delay);
    this.retryCount++;
  }

  // Método para mudar as assinaturas (unsubscribe e depois subscribe)
  switchSubscription(newSymbols) {
    this.unsubscribeFromAllSymbols(); // Desinscreve de todos os símbolos antigos
    this.subscribeToSymbols(newSymbols); // Inscreve os novos símbolos
  }

  subscribeToSymbols(symbols) {
    symbols.forEach(symbol => {
      if (!this.subscribedSymbols.has(symbol)) {
        this.subscribedSymbols.set(symbol, { count: 1 });
      } else {
        this.subscribedSymbols.get(symbol).count++;
      }
    });
    this.sendSubscribedSymbols();
  }

  unsubscribeFromSymbols(symbols) {
    const symbolsToUnsubscribe = [];

    symbols.forEach(symbol => {
      if (this.subscribedSymbols.has(symbol)) {
        const subscription = this.subscribedSymbols.get(symbol);
        subscription.count--;
        if (subscription.count <= 0) {
          this.subscribedSymbols.delete(symbol);
          symbolsToUnsubscribe.push(symbol);  // Adiciona o símbolo à lista de unsubscribe
        }
      }
    });

    // Envia a mensagem de unsubscribe se houver símbolos a desinscrever
    if (symbolsToUnsubscribe.length > 0) {
      const message = JSON.stringify({ type: "tick", unsubscribe: symbolsToUnsubscribe });
      this.sendMessage(message);  // Envia a mensagem via WebSocket
    }
  }

  unsubscribeFromAllSymbols(symbolsToUnsubscribe) {
    // const symbolsToUnsubscribe = Array.from(this.subscribedSymbols.keys());
    if (symbolsToUnsubscribe.length > 0) {
      const message = JSON.stringify({ type: "tick", unsubscribe: symbolsToUnsubscribe });
      this.sendMessage(message);
    }
    this.subscribedSymbols.clear();
  }

  sendSubscribedSymbols() {
    console.log('WebSocketManager: Sending subscribed symbols');
    const symbols = Array.from(this.subscribedSymbols.keys());
    if (symbols.length > 0) {
      const message = JSON.stringify({ type: "tick", subscribe: symbols });
      this.sendMessage(message);
    }
  }

  sendMessage(message) {
    if (this.isConnected()) {
      try {
        // Se a mensagem não for uma string, converte para JSON
        const formattedMessage = typeof message === 'string' ? message : JSON.stringify(message);
        this.socket.send(formattedMessage);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    } else {
      console.log('WebSocketManager: Not connected, queueing message');
      this.messageQueue.push(message);
      this.connect();
    }
  }


  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  close() {
    if (this.socket) {
      console.log('WebSocketManager: Closing connection');
      clearInterval(this.pingInterval);
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
      this.socket = null;
    }
    this.subscribedSymbols.clear();
  }
}

export default WebSocketManager;
